import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { cloneDeep, debounce } from 'lodash-es';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { firstValueFrom } from 'rxjs';
import { EnfafModalComponent } from '../../../../../../../components/enfaf-modal/enfaf-modal.component';
import { ApiService } from '../../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { Course } from '../../../../../../../models/interfaces/course.interface';
import { Student } from '../../../../../../../models/interfaces/student.interface';
import { UtilsService } from '../../../../../../../shared/utils/utils.service';

@Component({
  selector: 'app-modal-course-students',
  imports: [
    CommonModule,
    FormsModule,
    InfiniteScrollModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    EnfafModalComponent
  ],
  templateUrl: './modal-course-students.component.html',
  styleUrl: './modal-course-students.component.scss'
})
export class ModalCourseStudentsComponent implements OnInit {
  isLoadingCourse = true;
  courseId!: string;
  courseName!: string;
  students: Array<Student> = [];
  filteredStudents: Array<Student> = [];
  page = -1;
  pageSize = 20;
  screenWidth = 0;
  studentsLimit = 0;
  infiniteScrollDisabled = false;
  disableInfiniteCauseFilter = false;
  studentToSearch: any = {
    name: null,
    classesCompleted: null,
    finished: null
  };
  isSearchingStudent = false;
  isLoadingScroll = false;

  private debouncedSearchStudent = debounce(() => {
    this.filterStudentRemotely();
  }, 1500);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Course,
    private dialogRef: MatDialogRef<ModalCourseStudentsComponent>,
    private api: ApiService,
    private toast: ToastService,
    public utils: UtilsService
  ) {}

  @HostListener('window:resize', ['$event'])
  getScreenSize(event = null) {
    this.screenWidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.fetchCourseStudents();
  }

  async fetchCourseStudents(): Promise<void> {
    try {
      this.courseId = this.data && this.data.courseId;
      if (!this.courseId) return this.closeModal();

      const params = {
        page: ++this.page,
        pageSize: this.pageSize,
        courseId: this.courseId
      };
      const res = await firstValueFrom(this.api.fetchCourseStudents(params));
      if (params.page === 0) {
        this.students = res.students.rows;
        this.filteredStudents = cloneDeep(this.students);
        this.studentsLimit = res.students.count;
        this.courseName = res.course.name;
      } else {
        if (this.students.length === this.filteredStudents.length) {
          const newRows = cloneDeep(res.students.rows);
          this.filteredStudents.push(...newRows);
        }
        this.students.push(...res.students.rows);
      }
      this.setDisableInfinite();
      this.isLoadingCourse = false;
    } catch (error) {
      this.isLoadingCourse = false;
      this.toast.presentErrorToast('Hubo un error consultando estudiantes de este curso');
    }
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  setDisableInfinite(): void {
    this.infiniteScrollDisabled = this.students.length >= this.studentsLimit || this.disableInfiniteCauseFilter;
  }

  async onScrollDown(): Promise<void> {
    this.isLoadingScroll = true;
    this.disableInfiniteCauseFilter = true;
    this.setDisableInfinite();
    await this.fetchCourseStudents();
    this.isLoadingScroll = false;
    this.disableInfiniteCauseFilter = false;
    this.setDisableInfinite();
  }

  filterStudent(): void {
    this.studentToSearch.name = this.studentToSearch.name ? this.studentToSearch.name.trim().toLowerCase() : null;
    if (
      !this.utils.hasSomething(this.studentToSearch.name) &&
      !this.utils.hasSomething(this.studentToSearch.classesCompleted) &&
      !this.utils.hasSomething(this.studentToSearch.finished)
    ) {
      this.debouncedSearchStudent();
      this.isSearchingStudent = false;
      this.filteredStudents = cloneDeep(this.students);
      this.disableInfiniteCauseFilter = false;
      this.setDisableInfinite();
      return;
    }

    this.isSearchingStudent = true;
    this.debouncedSearchStudent();
  }

  async filterStudentRemotely(): Promise<void> {
    const defaultError = 'Hubo un error buscando estudiante';
    try {
      if (
        !this.utils.hasSomething(this.studentToSearch.name) &&
        !this.utils.hasSomething(this.studentToSearch.classesCompleted) &&
        !this.utils.hasSomething(this.studentToSearch.finished)
      ) {
        return;
      }

      const body = {
        courseId: this.courseId,
        text: this.studentToSearch.name,
        classesCompleted: this.studentToSearch.classesCompleted,
        finished: this.studentToSearch.finished
      };
      const response = await firstValueFrom(this.api.filterCourseStudent(body));
      this.isSearchingStudent = false;
      this.filteredStudents = response.students?.length ? response.students : [];
      this.disableInfiniteCauseFilter = true;
      this.setDisableInfinite();
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      return errorMessage ? this.toast.presentErrorToast(errorMessage) : this.toast.presentErrorToast(defaultError);
    }
  }

  async downloadBiomechanisStudentDiploma(i: number, userId: string): Promise<void> {
    try {
      this.filteredStudents[i].downloadingDiploma = true;
      const params: any = { courseId: this.courseId, userId };
      await this.api.downloadBiomechanisStudentDiploma(params);
      this.filteredStudents[i].downloadingDiploma = false;
      this.toast.presentSuccessToast('Diploma descargado');
    } catch (errorResponse: any) {
      this.filteredStudents[i].downloadingDiploma = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error descargando diploma');
    }
  }

  async downloadPersonalTrainerCourseDiploma(i: number, userId: string): Promise<void> {
    try {
      this.filteredStudents[i].downloadingDiploma = true;
      const params: any = { courseId: this.courseId, userId };
      await this.api.downloadPersonalTrainerCourseDiploma(params);
      this.filteredStudents[i].downloadingDiploma = false;
      this.toast.presentSuccessToast('Diploma descargado');
    } catch (errorResponse: any) {
      this.filteredStudents[i].downloadingDiploma = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error descargando diploma');
    }
  }

  clearFilters(): void {
    this.studentToSearch = {
      name: null,
      classesCompleted: null,
      finished: null
    };
    this.isSearchingStudent = false;
    this.filteredStudents = cloneDeep(this.students);
    this.disableInfiniteCauseFilter = false;
    this.setDisableInfinite();
  }

  async completeCourse(i: number, userId: string): Promise<void> {
    try {
      this.filteredStudents[i].isCompletingCourse = true;
      const body = {
        userId,
        courseId: this.courseId
      };
      const response = await firstValueFrom(this.api.completeStudentCourse(body));
      this.filteredStudents[i].isCompletingCourse = false;
      const { student } = response;
      this.filteredStudents.splice(i, 1, student);

      const indexFound = this.students.findIndex((item) => item.userId === student.userId);
      if (indexFound === -1) return;

      this.students.splice(indexFound, 1, student);
    } catch (errorResponse: any) {
      this.filteredStudents[i].isCompletingCourse = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error completando curso de estudiante');
    }
  }

  async downloadStudentCourseDocuments(student: Student): Promise<void> {
    try {
      student.isDownloadingDocuments = true;
      const params = {
        courseId: this.courseId,
        userId: student.userId
      };
      const fullname = `${student.name} ${student.lastname}`;
      await this.api.downloadStudentCourseDocuments(params, fullname, this.courseName);
      student.isDownloadingDocuments = false;
    } catch (error) {
      student.isDownloadingDocuments = false;
      this.toast.presentErrorToast('Hubo un error descargando recursos de esta sección');
    }
  }

  async downloadCourseStudentDiploma(i: number, userId: string): Promise<void> {
    try {
      this.filteredStudents[i].downloadingDiploma = true;
      const params: any = { courseId: this.courseId, userId };
      await this.api.downloadCourseStudentDiploma(params);
      this.filteredStudents[i].downloadingDiploma = false;
      this.toast.presentSuccessToast('Diploma descargado');
    } catch (errorResponse: any) {
      this.filteredStudents[i].downloadingDiploma = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error descargando diploma');
    }
  }

  generateEnrollmentOrder(studentId: string) {
    this.api.exportCourseStudentsEnrollmentOrders(this.courseId, [studentId]);
  }
}
